import { watch } from 'vue'
import { useConsentStore } from '@/entities/consent'
import {
  isGoogleAnalyticsLoadable,
  loadGoogleAnalytics,
  resetGoogleAnalytics,
} from '@/shared/lib/analytics/google-analytics'
import {
  isYandexMetricaLoadable,
  loadYandexMetrica,
  resetYandexMetrica,
} from '@/shared/lib/analytics/yandex-metrica'

/**
 * Install the analytics pipeline.
 *
 * Load ordering contract, applied per tracker:
 *   1. Production-domain gate (inside each loader) — non-prod never runs
 *      tracking even when consent says analytics:true. Developers can
 *      freely accept cookies in dev without polluting the dashboards.
 *   2. Consent gate — on production, trackers load only once the user
 *      has granted the `analytics` category. Rejecting or leaving the
 *      decision pending keeps them dormant.
 *
 * The watcher fires with `{ immediate: true }` so returning visitors who
 * already consented in a previous session pick up tracking on the very
 * first page load — no delay until the banner re-triggers.
 *
 * Must be called AFTER `app.use(pinia)` so `useConsentStore()` resolves.
 */
export function installAnalytics(): void {
  // Skip the whole consent wiring on non-production hosts. Each loader
  // would short-circuit anyway, but avoiding the watch cuts a tiny bit
  // of noise in dev and keeps the Pinia devtools timeline clean.
  const anyTrackerLoadable
    = isYandexMetricaLoadable() || isGoogleAnalyticsLoadable()
  if (!anyTrackerLoadable)
    return

  const consent = useConsentStore()

  // Hydrate from localStorage (and, if enabled, the backend). The
  // localStorage read is synchronous, so by the time the watch below
  // evaluates its source for the immediate tick, the persisted decision
  // is already in place. The backend sync is fire-and-forget.
  void consent.initialize()

  watch(
    () => consent.isCategoryGranted('analytics'),
    (granted) => {
      if (granted) {
        loadYandexMetrica()
        loadGoogleAnalytics()
      }
      else {
        resetYandexMetrica()
        resetGoogleAnalytics()
      }
    },
    { immediate: true },
  )
}
